import {useFormikContext} from 'formik';
import {ChangeEvent, useEffect, useMemo} from 'react';
import Button from '../../../../../../../components/button';
import FormikContextInputField from '../../../../../../../components/formikFileds/FormikContextInputField';
import CustomCheckbox from '../../../../../../../components/mazraeKit/customCheckbox';
import MainFooter from '../../../../../../../components/mazraeKit/footers/footerMain';
import {WarrantyDocumentType} from '../../../../../../../services/hookService/getLoanRequestRule/types';
import CompanyAddressData from '../../../components/CompanyAddressData';
import {ConditionalField} from '../../../components/CondiitonalField';
import {IBankDocsGeneralForm, IPersonFormData} from '../../LoanDocsPage';
import EditPersonForm from './EditPersonForm';
import {
  areAllPersonsComplete,
  checkPersonComplete,
  PersonPrefix,
} from './MultiPersonValidation';
import PersonPreviewCard from './PersonPreviewCard';

interface MultiPersonPreviewProps {
  warrantyType: WarrantyDocumentType;
  isSubmitting: boolean;
  editingPerson: PersonPrefix | null;
  setEditingPerson: (person: PersonPrefix | null) => void;
  loanRequestRules?: any;
  loanRule?: any;
  loanRequestGuid?: string;
}

const MultiPersonPreview = ({
  warrantyType,
  isSubmitting,
  editingPerson,
  setEditingPerson,
  loanRequestRules,
  loanRule,
  loanRequestGuid,
}: MultiPersonPreviewProps) => {
  const {values, setFieldValue, isValid, setValues} =
    useFormikContext<IBankDocsGeneralForm>();

  // Get global values
  const globalParcelTrackingCode = values.global?.parcel_tracking_code;
  const globalCheckDeliveryMethod = values.global?.check_delivery_method;

  const allRequiredComplete = () => {
    return areAllPersonsComplete(values, warrantyType);
  };

  const handleEdit = (prefix: PersonPrefix) => {
    setEditingPerson(prefix);
  };

  const handleComplete = () => {
    setEditingPerson(null);
  };

  const isGuarantor0Complete = checkPersonComplete(values, 'Guarantor-0');
  const isGuarantor1Complete = checkPersonComplete(values, 'Guarantor-1');
  const isSelfComplete = checkPersonComplete(values, 'self');

  // Memoize hasPaperCheckInForm to prevent infinite re-renders
  const hasPaperCheckInForm = useMemo((): boolean => {
    return Object.values(values).some(
      (personData: IPersonFormData) => personData?.checkType === 'paper',
    );
  }, [values]);

  const isAllFormDataComplete = useMemo((): boolean => {
    switch (warrantyType) {
      case WarrantyDocumentType.OWN_CHECK:
        return isSelfComplete;

      case WarrantyDocumentType.ONE_GUARANTOR:
        return isGuarantor0Complete;

      case WarrantyDocumentType.TWO_GUARANTORS:
        return isGuarantor0Complete && isGuarantor1Complete;

      case WarrantyDocumentType.OWN_CHECK_SINGLE_GUARANTOR:
        return isSelfComplete && isGuarantor0Complete;

      case WarrantyDocumentType.OWN_CHECK_TWO_GUARANTORS:
        return isSelfComplete && isGuarantor0Complete && isGuarantor1Complete;

      default:
        return false;
    }
  }, [
    warrantyType,
    isSelfComplete,
    isGuarantor0Complete,
    isGuarantor1Complete,
  ]);

  const shouldShowPaperCheckSection =
    isAllFormDataComplete && hasPaperCheckInForm;

  const isCheckSent = globalCheckDeliveryMethod === 'sent';

  // Reset check_delivery_method and parcel_tracking_code when conditions become false
  useEffect(() => {
    if (!shouldShowPaperCheckSection) {
      setFieldValue('global.check_delivery_method', '');
      setFieldValue('global.parcel_tracking_code', '');
    }
  }, [shouldShowPaperCheckSection, setFieldValue]);

  const handleCheckboxChange = (
    checked: boolean,
    method: 'sent' | 'delivered',
  ) => {
    if (checked) {
      setValues((prev: any) => ({
        ...prev,
        global: {
          ...prev.global,
          check_delivery_method: method,
          parcel_tracking_code:
            method !== 'sent' ? '' : prev.global.parcel_tracking_code,
        },
      }));
    } else {
      if (globalCheckDeliveryMethod === method) {
        setValues((prev: any) => ({
          ...prev,
          global: {
            ...prev.global,
            check_delivery_method: '',
            parcel_tracking_code: '',
          },
        }));
      }
    }
  };

  const shouldDisableButton = useMemo(() => {
    if (!allRequiredComplete() || !isValid || isSubmitting) return true;

    if (hasPaperCheckInForm) {
      if (!globalCheckDeliveryMethod) return true;
      if (globalCheckDeliveryMethod === 'sent' && !globalParcelTrackingCode)
        return true;
    }

    return false;
  }, [
    allRequiredComplete,
    isSubmitting,
    hasPaperCheckInForm,
    globalCheckDeliveryMethod,
    globalParcelTrackingCode,
  ]);

  // Show edit form if editing a person
  if (editingPerson) {
    return (
      <EditPersonForm
        personPrefix={editingPerson}
        onComplete={handleComplete}
        loanRequestRules={loanRequestRules}
        loanRule={loanRule}
        loanRequestGuid={loanRequestGuid || ''}
      />
    );
  }

  return (
    <div className="flex flex-col gap-2 pb-20 mt-2">
      <div className="gap-2">
        <h5 className="text-sm font-medium text-gray-800">اطلاعات ضمانت</h5>
        <div className="text-sm text-gray-600">
          بر اساس رتبه اعتباری شما و مبلغ وام درخواستی مدارک ضمانت زیر را تکمیل
          کنید.
        </div>
      </div>
      {(warrantyType === WarrantyDocumentType.OWN_CHECK ||
        warrantyType === WarrantyDocumentType.OWN_CHECK_SINGLE_GUARANTOR ||
        warrantyType === WarrantyDocumentType.OWN_CHECK_TWO_GUARANTORS) && (
        <PersonPreviewCard
          loanRequestRules={loanRequestRules}
          personPrefix="self"
          onEdit={handleEdit}
        />
      )}

      {(warrantyType === WarrantyDocumentType.ONE_GUARANTOR ||
        warrantyType === WarrantyDocumentType.TWO_GUARANTORS ||
        warrantyType === WarrantyDocumentType.OWN_CHECK_SINGLE_GUARANTOR ||
        warrantyType === WarrantyDocumentType.OWN_CHECK_TWO_GUARANTORS) && (
        <PersonPreviewCard
          loanRequestRules={loanRequestRules}
          personPrefix="Guarantor-0"
          onEdit={handleEdit}
        />
      )}

      {(warrantyType === WarrantyDocumentType.TWO_GUARANTORS ||
        warrantyType === WarrantyDocumentType.OWN_CHECK_TWO_GUARANTORS) && (
        <PersonPreviewCard
          loanRequestRules={loanRequestRules}
          personPrefix="Guarantor-1"
          onEdit={handleEdit}
        />
      )}

      {/* Company Address and Check Sent Section */}
      <ConditionalField show={shouldShowPaperCheckSection}>
        <div className="flex flex-col mt-3 space-y-4">
          <CompanyAddressData />
          <CustomCheckbox
            customClass="gap-1.5"
            name="global.check_delivery_sent"
            title="اصل چک را ارسال کردم"
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              handleCheckboxChange(e.target.checked, 'sent')
            }
            checked={globalCheckDeliveryMethod === 'sent'}
          />
          <CustomCheckbox
            customClass="gap-1.5 pb-2"
            name="global.check_delivery_delivered"
            title="اصل چک را به فروشگاه طرف قرارداد تحویل دادم"
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              handleCheckboxChange(e.target.checked, 'delivered')
            }
            checked={globalCheckDeliveryMethod === 'delivered'}
          />
        </div>
      </ConditionalField>

      {/* Parcel Tracking Section - only show when delivery method is 'sent' */}
      <ConditionalField show={shouldShowPaperCheckSection && isCheckSent}>
        <div className="mt-3 space-y-4">
          <FormikContextInputField
            name="global.parcel_tracking_code"
            label="کد رهگیری مرسوله"
            placeholder="کد رهگیری مرسوله را وارد کنید"
            required
            type="text"
            inputMode="numeric"
          />
        </div>
      </ConditionalField>

      <div className="fixed bottom-0 left-0 right-0 z-50 flex flex-col w-full gap-2 bg-white">
        <div className="p-3">
          <Button
            type="submit"
            form="loan-docs-form"
            disabled={shouldDisableButton}
            isLoading={isSubmitting}
            className="w-full"
          >
            تایید
          </Button>
        </div>

        <MainFooter isScrolling={false} />
      </div>
    </div>
  );
};

export default MultiPersonPreview;
